import { useMemo } from 'react';
import { Text } from '@components/base/display';
import { Select, TextField } from '@components/base/inputs';
import { Alert, Spinner } from '@components/base/feedback';
import type { EntityFormConfig, FieldConfig } from '@components/integrated/entity-form';
import {
  applyLayoutOverrides,
  buildPublishedConfigFromEntityForm,
  usePublishLayoutConfig,
  withComponentRules,
} from '@components/shared/configurable';
import type { LayoutOverrides } from '@components/shared/configurable';
import type { VeosDocumentRow, VeosClassementOption } from '../../api/documentsService';
import {
  CLASSEMENT_FIELD,
  buildClassementConfig,
  useDocumentClassement,
} from './documentClassement';

// ---------------------------------------------------------------------------
// Presentational + registered parts of the shared "Classement + Libellé"
// autosave form. The non-component primitives (config builder, autosave hook,
// stable keys) live in `./documentClassement`. Both PDFs/images
// (`DocumentPreview`) and — per mahhp 22268 — emails (`EmailClassementForm`)
// render the same two inputs so renaming works "pareil".
// ---------------------------------------------------------------------------

interface DocumentClassementInputsProps {
  /** Merged (override-applied) config driving each input's label / placeholder / order / visibility. */
  formConfig: EntityFormConfig;
  classementOptions: VeosClassementOption[];
  cdDs: string;
  libelle: string;
  onSelectChange: (next: string) => void;
  onLibelleChange: (next: string) => void;
  onLibelleBlur: () => void;
}

/**
 * The two-input row (Classement `<Select>` + Libellé `<TextField>`), rendered in
 * merged-`order` sort order and gated on each field's merged `visible`. Labels /
 * placeholders flow from `formConfig`, never inline literals — so overrides
 * retext / hide / reorder them.
 */
export function DocumentClassementInputs({
  formConfig,
  classementOptions,
  cdDs,
  libelle,
  onSelectChange,
  onLibelleChange,
  onLibelleBlur,
}: Readonly<DocumentClassementInputsProps>) {
  const classementSelectOptions = classementOptions.map((o) => ({
    value: o.CD_DS,
    label: o.LIB_DS,
  }));

  const renderInput = (name: string, field: FieldConfig) => {
    if (name === CLASSEMENT_FIELD.classement) {
      return (
        <Select
          label={field.label}
          options={classementSelectOptions}
          value={cdDs}
          onChange={(val) => {
            const next = Array.isArray(val) ? (val[0] ?? '') : val;
            onSelectChange(next);
          }}
          placeholder={field.placeholder}
          size="sm"
        />
      );
    }
    if (name === CLASSEMENT_FIELD.libelle) {
      return (
        <TextField
          label={field.label}
          value={libelle}
          onChange={(e) => {
            onLibelleChange(e.target.value);
          }}
          onBlur={onLibelleBlur}
          placeholder={field.placeholder}
          size="sm"
        />
      );
    }
    return null;
  };

  const orderedInputs = (
    [
      [CLASSEMENT_FIELD.classement, formConfig.fields[CLASSEMENT_FIELD.classement]],
      [CLASSEMENT_FIELD.libelle, formConfig.fields[CLASSEMENT_FIELD.libelle]],
    ] as Array<[string, FieldConfig | undefined]>
  )
    .filter(
      (entry): entry is [string, FieldConfig] =>
        entry[1] !== undefined && entry[1].visible !== false,
    )
    .sort(
      (a, b) =>
        (a[1].order ?? Number.MAX_SAFE_INTEGER) - (b[1].order ?? Number.MAX_SAFE_INTEGER),
    );

  return (
    <div className="flex flex-col sm:flex-row gap-4 sm:items-end">
      {orderedInputs.map(([name, field]) => (
        <div key={name} className="flex-1">
          {renderInput(name, field)}
        </div>
      ))}
    </div>
  );
}

/** Small "Enregistrement…" indicator + error, shared by both hosts. */
export function DocumentClassementStatus({
  isSaving,
  saveError,
}: Readonly<{ isSaving: boolean; saveError: string | null }>) {
  return (
    <>
      {isSaving && (
        <div className="flex items-center gap-2">
          <Spinner size="sm" />
          <Text as="span" size="xs" color="muted">
            Enregistrement…
          </Text>
        </div>
      )}
      {saveError && <Alert variant="error">{saveError}</Alert>}
    </>
  );
}

// ---------------------------------------------------------------------------
// Email-side registered form (mahhp 22268 — "renommer les emails")
//
// PDFs/images already expose the classement/libellé form (`DocumentPreview`);
// emails did not. This registers the same pair as its own configurable
// component so the email detail view can re-categorise / rename an email with
// the exact same `EXT_MODIF_GED` round-trip (`TYPE = row.API`).
// ---------------------------------------------------------------------------

interface EmailClassementFormProps {
  row: VeosDocumentRow;
  classementOptions: VeosClassementOption[];
  /** Refreshes the host document list after a save (page-wired; optional for the registry schema). */
  onSaved?: () => void;
  /** Per-tenant layout overrides injected by `withComponentRules` (the `fields` bucket). */
  _layoutOverrides?: LayoutOverrides;
}

export function EmailClassementForm({
  row,
  classementOptions,
  onSaved,
  _layoutOverrides,
}: Readonly<EmailClassementFormProps>) {
  // `TYPE = row.API` ("EMAIL" for sent, "DOC" for received emails) — exactly the
  // GED contract, so renaming works "pareil" as for PDFs (per the ticket).
  const { cdDs, libelle, isSaving, saveError, setLibelle, onSelectChange, onLibelleBlur } =
    useDocumentClassement(row, row.API, onSaved);

  const baseConfig = useMemo(() => buildClassementConfig('claimflow.emailClassement.form'), []);
  const formConfig = useMemo(
    () => applyLayoutOverrides(baseConfig, _layoutOverrides),
    [baseConfig, _layoutOverrides],
  );

  usePublishLayoutConfig(
    'claimflow.emailClassement',
    buildPublishedConfigFromEntityForm(baseConfig, 'Email Classement'),
  );

  return (
    <div className="flex flex-col gap-4">
      <DocumentClassementInputs
        formConfig={formConfig}
        classementOptions={classementOptions}
        cdDs={cdDs}
        libelle={libelle}
        onSelectChange={onSelectChange}
        onLibelleChange={setLibelle}
        onLibelleBlur={onLibelleBlur}
      />
      <DocumentClassementStatus isSaving={isSaving} saveError={saveError} />
    </div>
  );
}

/**
 * Rule-aware wrapped `EmailClassementForm`. `EmailDetail` renders this constant
 * so admin-authored `renameField` / `hideField` / `reorderFields` /
 * `setFieldPlaceholder` rules on the `classement` / `libelle` keys apply without
 * a code deploy. Co-located with the raw component (registration lives in
 * `register.ts`, which imports the raw `EmailClassementForm`).
 *
 * The `react-refresh/only-export-components` disable is the same intentional
 * co-location trade-off used by the other claimflow `*Configurable` wrappers.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const EmailClassementFormConfigurable = withComponentRules(
  EmailClassementForm,
  'claimflow.emailClassement',
);
